// ── What Happened ──
export const WHAT_HAPPENED = [
{ id: "MANAGER_UNFAIR", label: "My manager treated me unfairly", emoji: "😤", accentColor: "#FF6B35" },
{ id: "PROMOTION", label: "I was passed over for promotion", emoji: "📈", accentColor: "#B8A0FF" },
{ id: "UNDERPAID", label: "I think I'm underpaid", emoji: "💰", accentColor: "#F4C95D" },
{ id: "BIAS", label: "I experienced bias", emoji: "⚖️", accentColor: "#6FA8DC" },
{ id: "CREDIT_STOLEN", label: "Someone else got credit for my work", emoji: "🏆", accentColor: "#D98CB3" },
{ id: "WORKLOAD", label: "My workload became unreasonable", emoji: "🔥", accentColor: "#F26B5B" },
{ id: "TREATED_DIFFERENTLY", label: "I was treated differently because of something about me", emoji: "🗣️", accentColor: "#7BC8A4" },
{ id: "FIRED", label: "I was fired / asked to leave", emoji: "🚪", accentColor: "#8B9DC3" },
{ id: "POLICY", label: "A company policy frustrated me", emoji: "🏢", accentColor: "#A0A0A0" },
{ id: "CANT_SPEAK_UP", label: "I couldn't speak up about something", emoji: "🤐", accentColor: "#7BC8A4" },
{ id: "RIDICULOUS", label: "Something ridiculous happened", emoji: "🤯", accentColor: "#C084FC" },
{ id: "OTHER", label: "Other", emoji: null, accentColor: "#94A3B8" },
] as const;

export type WhatHappenedId = (typeof WHAT_HAPPENED)[number]["id"];

// ── Influences ──
export const INFLUENCES = [
  { id: "FAVORITISM", label: "Manager favoritism" },
  { id: "POLITICS", label: "Office politics" },
  { id: "GENDER", label: "Gender" },
  { id: "AGE", label: "Age" },
  { id: "LOCATION", label: "Location" },
  { id: "NATIONALITY", label: "Nationality" },
  { id: "RACE", label: "Race / ethnicity" },
  { id: "RELIGION", label: "Religion" },
  { id: "DISABILITY", label: "Disability" },
  { id: "FAMILY", label: "Family / parental status" },
  { id: "SENIORITY", label: "Seniority" },
  { id: "RELATIONSHIP", label: "Personal relationship" },
  { id: "PERFORMANCE", label: "Performance perception" },
  { id: "COMPENSATION", label: "Compensation / budget" },
  { id: "COMPANY_POLICY", label: "Company policy" },
  { id: "DONT_KNOW", label: "I don't know" },
  { id: "SOMETHING_ELSE", label: "Something else" },
] as const;

export type InfluenceId = (typeof INFLUENCES)[number]["id"];

// ── Feelings ──
export const FEELINGS = [
  { id: "ANGRY", label: "Angry", emoji: "😡" },
  { id: "DISAPPOINTED", label: "Disappointed", emoji: "😞" },
  { id: "UNDERVALUED", label: "Undervalued", emoji: "😔" },
  { id: "FRUSTRATED", label: "Frustrated", emoji: "😤" },
  { id: "UNABLE_TO_SPEAK_UP", label: "Unable to speak up", emoji: "😶" },
  { id: "ANXIOUS", label: "Anxious", emoji: "😟" },
  { id: "CONFUSED", label: "Confused", emoji: "🤷" },
  { id: "INDIFFERENT", label: "Indifferent", emoji: "😐" },
  { id: "RIDICULOUS", label: "It was ridiculous", emoji: "😂" },
  { id: "HURT", label: "Hurt", emoji: "💔" },
  { id: "MOTIVATED_TO_LEAVE", label: "Motivated to leave", emoji: "🚀" },
  { id: "OTHER", label: "Other", emoji: "🌀" },
] as const;

export type FeelingId = (typeof FEELINGS)[number]["id"];

// ── Outcomes ──
export const OUTCOMES = [
  { id: "SPOKE_TO_MANAGER", label: "I spoke to my manager" },
  { id: "SPOKE_TO_HR", label: "I spoke to HR" },
  { id: "RAISED_WITH_LEADERSHIP", label: "I raised it with leadership" },
  { id: "DID_NOTHING", label: "I did nothing" },
  { id: "LOOKING_FOR_JOB", label: "I started looking for another job" },
  { id: "RESIGNED", label: "I resigned" },
  { id: "IMPROVED", label: "The situation improved" },
  { id: "NOTHING_CHANGED", label: "Nothing changed" },
  { id: "GOT_WORSE", label: "It got worse" },
  { id: "OTHER", label: "Other" },
  { id: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
] as const;

export type OutcomeId = (typeof OUTCOMES)[number]["id"];

// ── Functions ──
export const FUNCTIONS = [
  { id: "ENGINEERING", label: "Engineering" },
  { id: "PRODUCT", label: "Product" },
  { id: "DESIGN", label: "Design" },
  { id: "SALES", label: "Sales" },
  { id: "MARKETING", label: "Marketing" },
  { id: "FINANCE", label: "Finance" },
  { id: "HR", label: "HR" },
  { id: "OPERATIONS", label: "Operations" },
  { id: "CUSTOMER_SUPPORT", label: "Customer Support" },
  { id: "LEGAL", label: "Legal" },
  { id: "DATA_ANALYTICS", label: "Data / Analytics" },
  { id: "OTHER", label: "Other" },
  { id: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
] as const;

export type FunctionId = (typeof FUNCTIONS)[number]["id"];

// ── Seniority ──
export const SENIORITY = [
  { id: "INTERN", label: "Intern" },
  { id: "JUNIOR", label: "Junior" },
  { id: "MID_LEVEL", label: "Mid-level" },
  { id: "SENIOR", label: "Senior" },
  { id: "LEAD", label: "Lead" },
  { id: "MANAGER", label: "Manager" },
  { id: "DIRECTOR_PLUS", label: "Director+" },
  { id: "EXECUTIVE", label: "Executive" },
  { id: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
] as const;

export type SeniorityId = (typeof SENIORITY)[number]["id"];

// ── Time Periods ──
export const TIME_PERIODS = [
  { id: "THIS_WEEK", label: "This week" },
  { id: "THIS_MONTH", label: "This month" },
  { id: "ONE_TO_SIX_MONTHS", label: "1–6 months ago" },
  { id: "SIX_TO_TWELVE_MONTHS", label: "6–12 months ago" },
  { id: "MORE_THAN_A_YEAR", label: "More than a year ago" },
] as const;

export type TimePeriodId = (typeof TIME_PERIODS)[number]["id"];

// ── Reactions ──
export const REACTIONS = [
  { id: "SAME", label: "Same", emoji: "😳" },
  { id: "WTF", label: "WTF", emoji: "😡" },
  { id: "BEEN_THERE", label: "Been there", emoji: "🫂" },
  { id: "DEPENDS", label: "Depends", emoji: "🤔" },
] as const;

export type ReactionId = (typeof REACTIONS)[number]["id"];

// ── Helpers ──
export function findById<T extends { id: string }>(list: readonly T[], id: string): T | undefined {
  return list.find((item) => item.id === id);
}

export function idsAreValid<T extends { id: string }>(list: readonly T[], ids: string[]): boolean {
  return ids.every((id) => list.some((item) => item.id === id));
}
